import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateBasicDetails, setBasicDetailsCompleted, setLoading } from '@/store/slices/profileSlice';
import { CheckCircle } from 'lucide-react';

const BasicDetailsForm: React.FC = () => {
    const dispatch = useDispatch();
    const { basicDetails, loading } = useSelector((state: RootState) => state.profile);
    const { toast } = useToast();
    const [isEditing, setIsEditing] = useState(false);

    const [localData, setLocalData] = useState(basicDetails);

    const handleInputChange = (field: string, value: string) => {
        const updatedData = { ...localData, [field]: value };
        setLocalData(updatedData);
        dispatch(updateBasicDetails({ [field]: value }));
    };

    const validateForm = (): boolean => {
        const required = ['firstName', 'lastName', 'email', 'phone'];
        const missing = required.filter(field => !localData[field as keyof typeof localData]);

        if (missing.length > 0) {
            toast({
                title: 'Error',
                description: `Please fill all required fields: ${missing.join(', ')}`,
                variant: 'destructive'
            });
            return false;
        }

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(localData.email)) {
            toast({ title: 'Error', description: 'Please enter a valid email address', variant: 'destructive' });
            return false;
        }

        // Validate phone
        if (!/^\d{10}$/.test(localData.phone)) {
            toast({ title: 'Error', description: 'Please enter a valid 10-digit phone number', variant: 'destructive' });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        dispatch(setLoading(true));
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            dispatch(setBasicDetailsCompleted(true));
            toast({ title: 'Success', description: 'Basic details saved successfully!' });
        } catch (err) {
            toast({ title: 'Error', description: 'Failed to save basic details', variant: 'destructive' });
        } finally {
            dispatch(setLoading(false));
        }
    };


    const handleSave = () => {
        setIsEditing(false);
        toast({ title: 'Success', description: 'Profile updated successfully!' });

    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                        id="firstName"
                        value={localData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Enter first name"
                        readOnly={!isEditing}
                        className={`h-11 ${!isEditing ? 'bg-flup-surface-alt' : ''}`}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                        id="lastName"
                        value={localData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Enter last name"
                        readOnly={!isEditing}
                        className={`h-11 ${!isEditing ? 'bg-flup-surface-alt' : ''}`}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                    id="email"
                    type="email"
                    value={localData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    readOnly={!isEditing}
                    className={`h-11 ${!isEditing ? 'bg-flup-surface-alt' : ''}`}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                    id="phone"
                    type="tel"
                    value={localData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                    readOnly={!isEditing}
                    className={`h-11 ${!isEditing ? 'bg-flup-surface-alt' : ''}`}
                />
            </div>

            <div className="flex items-center space-x-2">
                <CheckCircle
                    className={`h-5 w-5 ${localData.profileVerified ? 'text-green-500' : 'text-muted-foreground'}`}
                />
                <Label className={localData.profileVerified ? 'text-green-500' : 'text-muted-foreground'}>
                    Profile Verified {localData.profileVerified ? '✓' : '(Not Verified)'}
                </Label>
            </div>

            {/* <div className="pt-4">
                <Button
                    onClick={handleSubmit}
                    className="w-full"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Basic Details'}
                </Button>
            </div> */}


            {/* Action Buttons */}
            <div className="flex gap-4 pt-6">
                {!isEditing ? (
                    <Button
                        onClick={() => setIsEditing(true)}
                        // className="flex-1 h-11 text-white button-effect font-medium"

                        className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium"
                    >
                        Edit Profile
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={handleSubmit}
                            disabled={loading}

                            className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium"
                        >

                            {loading ? 'Saving...' : 'Save Basic Details'}

                        </Button>
                        <Button
                            onClick={handleCancel}
                            variant="none"
                            className="flex-1 h-11 border-2 border-flup-hairline-2 text-flup-ink-2 font-medium hover:bg-flup-surface-alt"
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default BasicDetailsForm;